import { supabase } from '../lib/supabaseClient';

// «Dar de baja desde…» un horario (0304). La vista previa y la baja son dos RPC del servidor que
// comparten la MISMA regla: lo que la pantalla muestra antes de confirmar es lo que se aplica, y el
// bloqueo por gente aprobada lo decide la base, no el navegador.

export interface JornadaConPersona {
  fecha: string;
  persona: string;
}

export interface BajaPreview {
  desde: string;
  /** Tramo en que el horario rige HOY (ya recortado a la campaña). */
  vigenteDesde: string;
  vigenteHasta: string;
  /** Cómo queda `vigente_hasta` después de la baja. */
  vigenteHastaNuevo: string;
  /** La baja lo deja sin ningún día: se verá como «dado de baja». */
  quedaSinDias: boolean;
  yaDadoDeBaja: boolean;
  /** Jornadas que se quitan. */
  posiciones: number;
  fechas: string[];
  /** Postulaciones pendientes que pasan a rechazadas. */
  propuestas: number;
  /** Rechazadas y reemplazadas viejas, que se archivan con las jornadas. */
  otrasAsignaciones: number;
  /** Si hay alguna, NO se puede: primero se retiran o reemplazan en «Asignaciones». */
  aprobadas: JornadaConPersona[];
  /** Asignaciones no aprobadas que igual tienen asistencia, cierre, visitas o canjes: día trabajado. */
  conRegistros: (JornadaConPersona & { estado: string })[];
  bloqueada: boolean;
  /** No hay nada que quitar: el horario ya terminaba antes de esa fecha. */
  sinEfecto: boolean;
}

interface BajaPreviewDb {
  desde: string;
  vigente_desde: string;
  vigente_hasta: string;
  vigente_hasta_nuevo: string;
  queda_sin_dias: boolean;
  ya_dado_de_baja: boolean;
  posiciones: number;
  fechas: string[];
  propuestas: number;
  otras_asignaciones: number;
  aprobadas: JornadaConPersona[];
  con_registros: (JornadaConPersona & { estado: string })[];
  bloqueada: boolean;
  sin_efecto: boolean;
}

export async function previewBajaHorario(scheduleId: string, desde: string): Promise<BajaPreview> {
  const { data, error } = await supabase.rpc('schedule_baja_preview', { p_schedule: scheduleId, p_desde: desde });
  // El mensaje del servidor va tal cual: dice qué fecha y qué rango, y uno propio lo taparía.
  if (error) throw new Error(error.message);
  const d = data as BajaPreviewDb;
  return {
    desde: d.desde,
    vigenteDesde: d.vigente_desde,
    vigenteHasta: d.vigente_hasta,
    vigenteHastaNuevo: d.vigente_hasta_nuevo,
    quedaSinDias: d.queda_sin_dias,
    yaDadoDeBaja: d.ya_dado_de_baja,
    posiciones: Number(d.posiciones),
    fechas: d.fechas ?? [],
    propuestas: Number(d.propuestas),
    otrasAsignaciones: Number(d.otras_asignaciones),
    aprobadas: d.aprobadas ?? [],
    conRegistros: d.con_registros ?? [],
    bloqueada: d.bloqueada,
    sinEfecto: d.sin_efecto,
  };
}

export interface BajaResultado {
  bajaId: string;
  desde: string;
  vigenteHasta: string;
  quedaSinDias: boolean;
  posicionesQuitadas: number;
  propuestasRechazadas: number;
  asignacionesArchivadas: number;
}

export async function darDeBajaHorario(scheduleId: string, desde: string, motivo: string): Promise<BajaResultado> {
  const { data, error } = await supabase.rpc('schedule_dar_de_baja', {
    p_schedule: scheduleId, p_desde: desde, p_motivo: motivo,
  });
  if (error) throw new Error(error.message);
  const d = data as {
    baja: string; desde: string; vigente_hasta: string; queda_sin_dias: boolean;
    posiciones_quitadas: number; propuestas_rechazadas: number; asignaciones_archivadas: number;
  };
  return {
    bajaId: d.baja,
    desde: d.desde,
    vigenteHasta: d.vigente_hasta,
    quedaSinDias: d.queda_sin_dias,
    posicionesQuitadas: Number(d.posiciones_quitadas),
    propuestasRechazadas: Number(d.propuestas_rechazadas),
    asignacionesArchivadas: Number(d.asignaciones_archivadas),
  };
}

/** La primera jornada GENERADA del horario desde `desde` (inclusive), o null si no hay. */
export async function proximaJornadaDelHorario(scheduleId: string, desde: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('positions')
    .select('date')
    .eq('schedule_id', scheduleId)
    .gte('date', desde)
    .order('date')
    .limit(1);
  if (error) throw new Error(error.message);
  const filas = (data ?? []) as { date: string }[];
  return filas.length > 0 ? filas[0].date : null;
}

/** Cuántas jornadas generadas tiene el horario ANTES de `fecha`. Para no correr el inicio sobre días ya programados. */
export async function contarJornadasAntesDe(scheduleId: string, fecha: string): Promise<number> {
  const { count, error } = await supabase
    .from('positions')
    .select('id', { count: 'exact', head: true })
    .eq('schedule_id', scheduleId)
    .lt('date', fecha);
  if (error) throw new Error(error.message);
  return count ?? 0;
}

export interface BajaRegistro {
  id: string;
  scheduleId: string;
  desde: string;
  motivo: string;
  en: string;
}

/**
 * Las bajas de la campaña, la más reciente primero. La RLS se las da solo al dueño de la campaña y
 * al jefe de operaciones: a cualquier otro le devuelve CERO filas sin error, y la tabla de horarios
 * muestra entonces las fechas sin el motivo.
 */
export async function listBajasDeCampania(campaignId: string): Promise<BajaRegistro[]> {
  const { data, error } = await supabase
    .from('campaign_schedule_bajas')
    .select('id, schedule_id, desde, motivo, en')
    .eq('campaign_id', campaignId)
    .order('en', { ascending: false });
  if (error) throw new Error(error.message);
  return ((data ?? []) as { id: string; schedule_id: string; desde: string; motivo: string; en: string }[])
    .map((b) => ({ id: b.id, scheduleId: b.schedule_id, desde: b.desde, motivo: b.motivo, en: b.en }));
}

/** La última baja de cada horario (la lista viene de la más reciente a la más vieja). */
export function ultimaBajaPorHorario(bajas: BajaRegistro[]): Map<string, BajaRegistro> {
  const m = new Map<string, BajaRegistro>();
  for (const b of bajas) if (!m.has(b.scheduleId)) m.set(b.scheduleId, b);
  return m;
}
